import React, { createContext, useContext, useState, useEffect } from 'react';

const TextSizeContext = createContext();

export const useTextSize = () => {
  const context = useContext(TextSizeContext);
  if (!context) {
    throw new Error('useTextSize must be used within a TextSizeProvider');
  }
  return context;
};

// Text size configurations
const textSizeConfig = {
  small: {
    label: 'Small',
    labelCh: '小',
    rootFontSize: '14px',
    scale: 0.875,
    className: 'text-size-small'
  },
  medium: {
    label: 'Medium',
    labelCh: '中',
    rootFontSize: '16px',
    scale: 1,
    className: 'text-size-medium'
  },
  large: {
    label: 'Large',
    labelCh: '大',
    rootFontSize: '18px',
    scale: 1.125,
    className: 'text-size-large'
  }
};

// Default text size based on screen size
const getDefaultTextSize = () => {
  if (typeof window !== 'undefined') {
    const width = window.innerWidth;
    if (width < 768) return 'small'; // Mobile
    if (width < 1024) return 'medium'; // Tablet
    return 'large'; // Desktop
  }
  return 'medium';
};

export const TextSizeProvider = ({ children }) => {
  const [textSize, setTextSize] = useState(() => {
    // Try to get from localStorage first, then use default
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('textSize');
      return saved || getDefaultTextSize();
    }
    return 'medium'; // Default fallback
  });

  // Apply text size to document root
  useEffect(() => {
    const config = textSizeConfig[textSize];
    if (config && typeof window !== 'undefined') {
      document.documentElement.style.fontSize = config.rootFontSize;
      document.documentElement.className = document.documentElement.className
        .replace(/text-size-\w+/g, '') + ' ' + config.className;
      
      // Save to localStorage
      localStorage.setItem('textSize', textSize);
    }
  }, [textSize]);

  // Handle screen size changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      // Only auto-adjust if no manual selection has been made
      const hasManualSelection = localStorage.getItem('textSizeManual');
      if (!hasManualSelection) {
        const defaultSize = getDefaultTextSize();
        if (defaultSize !== textSize) {
          setTextSize(defaultSize);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [textSize]);

  // Change text size with manual flag
  const changeTextSize = (size) => {
    setTextSize(size);
    if (typeof window !== 'undefined') {
      localStorage.setItem('textSizeManual', 'true');
    }
  };

  // Reset to auto mode
  const resetToAuto = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('textSizeManual');
    }
    const defaultSize = getDefaultTextSize();
    setTextSize(defaultSize);
  };

  // Get current configuration
  const getCurrentConfig = () => textSizeConfig[textSize];

  // Check if manual selection is active
  const isManualSelection = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('textSizeManual') === 'true';
    }
    return false;
  };

  const value = {
    textSize,
    setTextSize: changeTextSize,
    resetToAuto,
    getCurrentConfig,
    isManualSelection,
    textSizeConfig,
    availableSizes: Object.keys(textSizeConfig)
  };

  return (
    <TextSizeContext.Provider value={value}>
      {children}
    </TextSizeContext.Provider>
  );
};

export default TextSizeProvider; 